import { Alert, Input, message, Skeleton, Space } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import ObjectPurposeApiService from '../../../api/ObjectPurposeApiService';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as GetObjectPurposeDetailsHandler from '../../../handlerModels/GetObjectPurposeDetailsHandler';
import * as SaveObjectPurposeDetailsHandler from '../../../handlerModels/SaveObjectPurposeDetailsHandler';
import ObjectPurposeDTO from '../../../models/ObjectPurposeDTO';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import BaseFormState from '../../../redux/bases/BaseFormState';
import LookupsUtil from '../../../utils/LookupsUtil';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import AuthorizedContent from '../../AuthorizedContent';
import ResetButton from '../../buttons/ResetButton';
import SaveButton from '../../buttons/SaveButton';

interface ObjectPurposeDetailsFormState extends BaseFormState {
  objectPurposeId: number;
  objectPurpose: ObjectPurposeDTO | null;
}

interface ObjectPurposeDetailsFormProps extends BaseFormProps {
  objectPurposeId: number;
  onSave?: (objectPurposeId: number) => void;
}

class ObjectPurposeDetailForm extends React.Component<ObjectPurposeDetailsFormProps, ObjectPurposeDetailsFormState> {
  private readonly _formRef = React.createRef<FormInstance>();
  private readonly _formItems = new Map<string, FormItemProps>()
    .set(ObjectPurposeDTO.name, {
      required: true,
      name: ObjectPurposeDTO.name,
      label: 'Name',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(150)
      ]
    })
    .set(ObjectPurposeDTO.code, {
      required: true,
      name: ObjectPurposeDTO.code,
      label: 'Code',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(10)
      ],
    });

  constructor(props: ObjectPurposeDetailsFormProps) {
    super(props);

    this.state = {
      objectPurposeId: 0,
      objectPurpose: ObjectPurposeDTO.create()
    };
  }

  componentDidMount() {
    this.setState({ loading: true });
    this.loadObjectPurpose();
  }

  componentDidUpdate(prevProps: ObjectPurposeDetailsFormProps) {
    if (this.props.objectPurposeId && this.props.objectPurposeId != prevProps.objectPurposeId) {
      this.setState({ objectPurposeId: this.props.objectPurposeId });
      this.loadObjectPurpose();
    }
  }

  public resetForm = () => {
    this._formRef.current?.resetFields();
    this.setState({ altered: false });
  }

  private loadObjectPurpose() {
    if (this.props.objectPurposeId == 0) {
      this.loadNew();
    }
    else {
      this.loadExisting();
    }
  }

  private loadExisting = () => {
    if (this.props.objectPurposeId) {
      ObjectPurposeApiService.getObjectPurposeDetails(this.props.objectPurposeId)
        .then((results: GetObjectPurposeDetailsHandler.Result) => {
          if (results && results.objectPurpose) {
            this.setState({ objectPurposeId: results.objectPurpose.id, objectPurpose: results.objectPurpose, loading: false });
          }

          this.resetForm();
        }).catch(() => {
          this.setState({ error: true });
        }).finally(() => {
          this.setState({ loading: false });
        });
    }
  }

  private loadNew = () => {
    this.setState({ objectPurpose: ObjectPurposeDTO.create(), loading: false });
  }

  private handleChange = () => {
    this.setState({ altered: true });
  }

  private handleSubmit = () => {
    this.setState({ submitting: true });

    const request = SaveObjectPurposeDetailsHandler.Request.create({
      objectPurposeId: this.state.objectPurposeId,
      objectPurpose: ObjectPurposeDTO.create(this._formRef ? (this._formRef.current as any).getFieldsValue() : null)
    });

    ObjectPurposeApiService.saveObjectPurposeDetails(request)
      .then((result: SaveObjectPurposeDetailsHandler.Result) => {
        this.setState({
          submitted: true,
          objectPurpose: result?.objectPurpose,
        });

        if (result?.succeeded) {
          message.success('Saved');

          if (this.props.onSave && result.objectPurpose) {
            this.props.onSave(result.objectPurpose.id);
            this.resetForm();
          }

          LookupsUtil.invalidateCache(ObjectPurposeDTO.className);
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          message.error('Object Purpose could not be saved. ' + this.state.message);
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Object Purpose could not be saved.');
      })
      .finally(() => {
        this.setState({ loading: false, submitting: false });
      });
  }

  render() {
    if (this.state.loading) {
      return <Skeleton active={true} />;
    }

    return (
      <Space size="small" direction="vertical">
        {this.renderErrors()}
        <Form
          ref={this._formRef}
          layout="vertical"
          onValuesChange={this.handleChange}
          onFinish={this.handleSubmit} requiredMark={true}>
          <FormItem
            {...this._formItems.get(ObjectPurposeDTO.code)}
            initialValue={this.state.objectPurpose?.code}
            {...ValidationUtil.getValidation(ObjectPurposeDTO.code, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>
          <FormItem
            {...this._formItems.get(ObjectPurposeDTO.name)}
            initialValue={this.state.objectPurpose?.name}
            {...ValidationUtil.getValidation(ObjectPurposeDTO.name, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>

          {this.renderSave()}
        </Form>
      </Space>
    );
  }

  renderSave() {
    if (this.props.isEditing) {
      return (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_OBJECT_PURPOSE]}>
          <Space direction={'horizontal'} >
            <SaveButton saving={this.state.saving} />
            <ResetButton onConfirm={this.resetForm} disabled={!this.state.altered} />
          </Space>
        </AuthorizedContent>
      );
    }
  }

  renderErrors() {
    if (this.state.error && this.state.submitted) {
      return <Alert type="error" message='Error' showIcon={true} description='There were errors submitting your request. Please review the fields below.' />;
    }
  }
}

export default ObjectPurposeDetailForm;
